/**
 * Copy: turning a range into a clipboard payload, and a payload into the three
 * clipboard flavours (Requirement 12.6).
 *
 * A copy writes three representations of the same block:
 *
 * - `application/json` — the internal {@link ClipPayload}, so values, formulas, and
 *   formatting survive a paste back into the app intact.
 * - `text/plain` — tab-separated value text, which is what every other spreadsheet
 *   and every text editor reads.
 * - `text/html` — a `<table>`, which is what a word processor and most spreadsheets
 *   prefer, carrying the same value text plus the bold/italic/underline and colour
 *   attributes a rich target can use.
 *
 * The payload's `inputs` hold *value* text (`filter.ts#cellText`, the stored value
 * rather than the formatted display), because that is what the values-only variant
 * writes and what an external target should receive: a formula's number, not its
 * source. `formulas` hold the sources separately, so nothing is lost.
 *
 * Style ids are workbook-scoped, so the payload carries the styles themselves in a
 * palette and indexes into it per cell. A paste interns the palette entries into the
 * destination's own style table, which is what lets a copy survive a paste into
 * another workbook — and what keeps the JSON flavour self-contained.
 */

import { cellText } from '../query/filter.js';
import type { FormatState } from '../format/manager.js';
import { styleById, styleKey, type CellStyle } from '../format/style.js';
import { cellStyleId, getCell, getFormula, type Workbook } from '../store/workbook.js';
import { normalizeRange, type Range } from '../types.js';
import type { ClipPayload } from '../command/types.js';
import { joinDelimited } from './delimited.js';

/** The MIME type the internal payload travels under. */
export const CLIP_JSON_TYPE = 'application/json';

/** Marker that identifies our own JSON on the clipboard. */
export const CLIP_MARKER = 'web-spreadsheet-app/clip';

/** The internal payload as it sits on the system clipboard. */
export interface ClipEnvelope {
  marker: typeof CLIP_MARKER;
  version: 1;
  payload: ClipPayload;
}

export interface BuildClipOptions {
  /** True for a cut: the paste clears the source block. */
  cut?: boolean;
}

/**
 * Snapshots a range into a payload (Requirement 12.6).
 *
 * Every cell contributes three things: its value text, its formula source or `null`,
 * and its style. The style palette is deduplicated by the same canonical key the
 * workbook's own interning uses, so a block of one thousand identically formatted
 * cells carries one style.
 */
export function buildClipPayload(
  wb: Workbook,
  fs: FormatState,
  range: Range,
  opts: BuildClipOptions = {},
): ClipPayload {
  const r = normalizeRange(range);
  const rows = r.r1 - r.r0 + 1;
  const cols = r.c1 - r.c0 + 1;
  const inputs: string[][] = [];
  const formulas: (string | null)[][] = [];
  const styles: number[][] = [];
  const palette: CellStyle[] = [];
  const index = new Map<string, number>();

  for (let i = 0; i < rows; i++) {
    const inputRow: string[] = [];
    const formulaRow: (string | null)[] = [];
    const styleRow: number[] = [];
    for (let j = 0; j < cols; j++) {
      const addr = { sheet: r.sheet, row: r.r0 + i, col: r.c0 + j };
      inputRow.push(cellText(getCell(wb, addr)));
      formulaRow.push(getFormula(wb, addr)?.source ?? null);
      const style = styleById(fs.table, cellStyleId(wb, addr));
      const key = styleKey(style);
      let slot = index.get(key);
      if (slot === undefined) {
        slot = palette.length;
        palette.push(style);
        index.set(key, slot);
      }
      styleRow.push(slot);
    }
    inputs.push(inputRow);
    formulas.push(formulaRow);
    styles.push(styleRow);
  }

  return {
    rows,
    cols,
    inputs,
    formulas,
    styles,
    palette,
    origin: { sheet: r.sheet, row: r.r0, col: r.c0 },
    ...(opts.cut === true ? { cut: true } : {}),
  };
}

/** The `text/plain` flavour: tab-separated value text. */
export function clipToText(payload: ClipPayload): string {
  return joinDelimited(payload.inputs);
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function styleAttribute(style: CellStyle | undefined): string {
  if (style === undefined) return '';
  const parts: string[] = [];
  if (style.bold) parts.push('font-weight:bold');
  if (style.italic) parts.push('font-style:italic');
  if (style.underline) parts.push('text-decoration:underline');
  if (style.color !== '#000000') parts.push(`color:${style.color}`);
  if (style.background !== 'transparent') parts.push(`background-color:${style.background}`);
  if (parts.length === 0) return '';
  return ` style="${escapeHtml(parts.join(';'))}"`;
}

/** The `text/html` flavour: a table carrying the same values plus font attributes. */
export function clipToHtml(payload: ClipPayload): string {
  const rows = payload.inputs
    .map((row, i) => {
      const cells = row
        .map((text, j) => {
          const slot = payload.styles?.[i]?.[j];
          const style = slot === undefined ? undefined : payload.palette?.[slot];
          return `<td${styleAttribute(style)}>${escapeHtml(text)}</td>`;
        })
        .join('');
      return `<tr>${cells}</tr>`;
    })
    .join('');
  return `<table><tbody>${rows}</tbody></table>`;
}

/** Wraps a payload for the system clipboard's JSON flavour. */
export function encodeClipEnvelope(payload: ClipPayload): string {
  const envelope: ClipEnvelope = { marker: CLIP_MARKER, version: 1, payload };
  return JSON.stringify(envelope);
}

/**
 * Reads our own JSON flavour back, or `null` when the text is not ours.
 *
 * Anything unrecognised — another app's JSON, a truncated payload, a hostile string —
 * returns `null` so the caller falls back to `text/plain` delimiter sniffing rather
 * than trusting a shape it has not checked.
 */
export function decodeClipEnvelope(text: string): ClipPayload | null {
  if (text.length === 0) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null) return null;
  const envelope = parsed as Partial<ClipEnvelope>;
  if (envelope.marker !== CLIP_MARKER) return null;
  const payload = envelope.payload;
  if (payload === undefined || !isClipPayload(payload)) return null;
  return payload;
}

/** Shape check for a payload arriving from outside this realm. */
export function isClipPayload(value: unknown): value is ClipPayload {
  if (typeof value !== 'object' || value === null) return false;
  const p = value as Partial<ClipPayload>;
  if (!Number.isInteger(p.rows) || !Number.isInteger(p.cols)) return false;
  if ((p.rows as number) < 0 || (p.cols as number) < 0) return false;
  if (!Array.isArray(p.inputs) || p.inputs.length !== p.rows) return false;
  for (const row of p.inputs) {
    if (!Array.isArray(row) || row.length !== p.cols) return false;
    if (row.some((cell) => typeof cell !== 'string')) return false;
  }
  return true;
}
